import { useContext, useEffect } from "react";
import { Table, Tag, Tabs, Tooltip, Typography, Badge } from "antd";
import dayjs from "dayjs";
import AntdTable from "../../../antd/Table/Table";
import { filtrationcontext } from "../../../../context/Filtercontext";
import { exportContext } from "../../../../context/ExportDataContext";
import trans, { isRTL } from "../../../../utils";
import { useReportTableColumns } from "../../ReportColumns/reportColumns";

const EXPIRY_COLORS = {
  EXPIRED:  { tag: "red",    bg: "#fff1f0", border: "#ffa39e", text: "#ff4d4f" },
  CRITICAL: { tag: "orange", bg: "#fff7e6", border: "#ffd591", text: "#fa8c16" },
  WARNING:  { tag: "gold",   bg: "#fffbe6", border: "#ffe58f", text: "#faad14" },
  NOTICE:   { tag: "blue",   bg: "#e6f7ff", border: "#91d5ff", text: "#1890ff" },
  VALID:    { tag: "green",  bg: "#f6ffed", border: "#b7eb8f", text: "#52c41a" },
  "N/A":    { tag: "default",bg: "#fafafa", border: "#d9d9d9", text: "#8c8c8c" },
};

const getStatusOptions = () => ["EXPIRED", "CRITICAL", "WARNING", "VALID"].map(s => ({
  text: trans(s), value: s,
}));

const StatusTag = ({ status }) => (
  <Tag color={EXPIRY_COLORS[status]?.tag || "default"} style={{ fontWeight: 600, borderRadius: 4 }}>
    {trans(status)}
  </Tag>
);

const DaysCell = ({ days }) => {
  if (days === null || days === undefined) return <span style={{ color: "#bbb" }}>—</span>;
  if (days < 0)
    return (
      <span style={{ color: "#ff4d4f", fontWeight: 600 }}>
        {Math.abs(days)} {trans("days")} {trans("ago")}
      </span>
    );
  if (days === 0)
    return <Tag color="orange" style={{ fontWeight: 600 }}>{trans("Today")}</Tag>;
  return (
    <span style={{ color: days <= 7 ? "#ff4d4f" : days <= 30 ? "#fa8c16" : "#52c41a", fontWeight: 600 }}>
      {days} {trans("days")}
    </span>
  );
};

// Compares two nullable arrays for equality (prevents spurious filter updates on page-only changes)
const arraysEqual = (a, b) => {
  const na = a ?? null;
  const nb = b ?? null;
  if (na === nb) return true;
  if (!na || !nb) return false;
  if (na.length !== nb.length) return false;
  return na.every((v, i) => String(v) === String(nb[i]));
};

const ProductExpirationReportTable = ({
  data,
  isLoading,
  currentPage,
  setCurrentPage,
  filters,
  setFilters,
  startDate,
  endDate,
}) => {
  const { setSort: SetSortFilter, limit, setLimit } = useContext(filtrationcontext);
  const { setExportedDataHeader, setExportedDataBody, setFileName, setStartDateE, setEndDateE, setFilters: setExportFilters } =
    useContext(exportContext);

  useEffect(() => {
    return () => { SetSortFilter({ by: "", order: "" }); };
  }, [SetSortFilter]);

  useEffect(() => {
    const items = data?.items || [];
    setFileName("Product Expiration Report");
    setExportedDataHeader([
      trans("SKU Name"), trans("Category"), trans("Location"), trans("State"), trans("City"), trans("Location Type"),
      trans("Item Place"), trans("Quantity"), trans("Shelf Price"),
      trans("Case Pack"), trans("Containers"),
      trans("Production Date"), trans("Expiry Date"), trans("Days Until Expiry"),
      trans("Status"), trans("Recorded By"), trans("Recorded At"),
    ]);
    setExportedDataBody(items.map((row) => [
      row.sku?.name || "",
      row.sku?.category?.name || "",
      row.poi?.name || "",
      row.poi?.state || "",
      row.poi?.city || "",
      row.poi?.type ? trans(row.poi.type) : "",
      row.trackingContext ? trans(row.trackingContext) : "",
      row.quantity ?? "",
      row.shelfPrice ?? "",
      row.trackingContext === "SHELF" ? (row.unitsPerContainer ?? "") : "",
      row.trackingContext === "SHELF" ? (row.shelfQtyContainers ?? "") : "",
      row.productionDate ? dayjs(row.productionDate).format("YYYY-MM-DD") : "",
      row.expiryDate     ? dayjs(row.expiryDate).format("YYYY-MM-DD")     : "",
      row.daysUntilExpiry ?? "",
      row.expiryStatus || "",
      row.createdBy?.name || "",
      row.createdAt ? dayjs(row.createdAt).format("YYYY-MM-DD HH:mm") : "",
    ]));
    setStartDateE(startDate || "");
    setEndDateE(endDate || "");
    // Sync all active filters into export context so the export handler can forward them
    setExportFilters({ ...filters });
  }, [data, startDate, endDate, filters, setFileName, setExportedDataHeader, setExportedDataBody, setStartDateE, setEndDateE, setExportFilters]);

  const skuOptions        = (data?.filterOptions?.skus          || []).map(s => ({ text: s.name, value: s.id }));
  const poiOptions        = (data?.filterOptions?.pois          || []).map(p => ({ text: p.name, value: p.id }));
  const recordedByOptions = (data?.filterOptions?.recordedByUsers || []).map(u => ({ text: u.name, value: u.id }));

  const categoryOptions = (data?.filterOptions?.categories || []).map(c => ({ text: isRTL ? c.name : (c.nameEn || c.name), value: c.id }));
  const poiTypeOptions  = (data?.filterOptions?.poiTypes   || []).map(t => ({ text: trans(t.value), value: t.value }));
  const stateOptions    = (data?.filterOptions?.states     || []).map(s => ({ text: trans(s.value), value: s.value }));
  // City options cascade off the selected state(s): each option carries its state.
  const allCityOptions  = data?.filterOptions?.cities || [];
  const cityOptions     = (
    filters.tableStates && filters.tableStates.length
      ? allCityOptions.filter(c => filters.tableStates.includes(c.state))
      : allCityOptions
  ).map(c => ({ text: trans(c.value), value: c.value }));
  const itemPlaceOptions = [
    { text: trans("SHELF"),     value: "SHELF" },
    { text: trans("WAREHOUSE"), value: "WAREHOUSE" },
  ];

  const tableSkuIds        = filters.tableSkuIds        || null;
  const tablePoiIds        = filters.tablePoiIds        || null;
  const tableRecordedByIds = filters.tableRecordedByIds || null;
  const tableStatuses      = filters.tableStatuses      || null;
  const tableCategories    = filters.tableCategories    || null;
  const tablePoiTypes      = filters.tablePoiTypes      || null;
  const tableItemPlaces    = filters.tableItemPlaces    || null;
  const tableStates        = filters.tableStates        || null;
  const tableCities        = filters.tableCities        || null;

  // ── Tab A: By Product ──────────────────────────────────────────────────────
  const groupedSkuOptions = (data?.grouped || []).map(g => ({ text: g.skuName, value: g.skuId }));

  const groupedColumns = [
    {
      title: trans("SKU Name"),
      dataIndex: "skuName",
      width: 260,
      filters: groupedSkuOptions,
      filterSearch: true,
      filterMultiple: true,
      onFilter: (value, record) => record.skuId === value,
      render: (val, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
          <div
            style={{
              width: 4, minHeight: 36, borderRadius: 2,
              backgroundColor: EXPIRY_COLORS[record.worstStatus]?.text || "#d9d9d9",
              flexShrink: 0,
            }}
          />
          <Tooltip title={val}>
            <span
              style={{
                flex: "1 1 0",
                minWidth: 0,
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              {val}
            </span>
          </Tooltip>
        </div>
      ),
    },
    {
      title: trans("Total Qty"),
      dataIndex: "totalQuantity",
      width: 110,
      align: "center",
      sorter: (a, b) => a.totalQuantity - b.totalQuantity,
      render: (val) => <span style={{ fontWeight: 600 }}>{val}</span>,
    },
    {
      title: trans("Worst Status"),
      dataIndex: "worstStatus",
      width: 140,
      align: "center",
      filters: getStatusOptions(),
      filterMultiple: true,
      onFilter: (value, record) => record.worstStatus === value,
      render: (val) => <StatusTag status={val} />,
      sorter: (a, b) => {
        const order = ["EXPIRED", "CRITICAL", "WARNING", "NOTICE", "VALID", "N/A"];
        return order.indexOf(a.worstStatus) - order.indexOf(b.worstStatus);
      },
    },
    {
      title: trans("Nearest Expiry"),
      dataIndex: "nearestExpiryDate",
      width: 140,
      align: "center",
      render: (val) => (val ? dayjs(val).format("YYYY-MM-DD") : "—"),
      sorter: (a, b) => {
        const aVal = a.nearestExpiryDate ? new Date(a.nearestExpiryDate).getTime() : Infinity;
        const bVal = b.nearestExpiryDate ? new Date(b.nearestExpiryDate).getTime() : Infinity;
        return aVal - bVal;
      },
      defaultSortOrder: "ascend",
    },
    {
      title: trans("Locations"),
      width: 100,
      align: "center",
      render: (_, record) => (
        <Badge
          count={record.locations?.length ?? 0}
          style={{ backgroundColor: "#1890ff" }}
          showZero
        />
      ),
    },
  ];

  const locationColumns = [
    {
      title: trans("Location"),
      dataIndex: "poiName",
      width: 200,
      render: (val) => <span style={{ fontWeight: 500 }}>{val}</span>,
    },
    {
      title: trans("State"),
      dataIndex: "state",
      width: 120,
      render: (val) =>
        val ? (
          <Tag color="blue" style={{ borderRadius: 10, fontWeight: 500 }}>{trans(val)}</Tag>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: trans("City"),
      dataIndex: "city",
      width: 130,
      render: (val) =>
        val ? (
          <Tag color="cyan" style={{ borderRadius: 10, fontWeight: 500 }}>{trans(val)}</Tag>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: trans("Item Place"),
      dataIndex: "trackingContext",
      width: 110,
      align: "center",
      render: (val) => {
        const isShelf = (val || "").toUpperCase() === "SHELF";
        return (
          <Tag color={isShelf ? "cyan" : "volcano"} style={{ borderRadius: 10, fontWeight: 600 }}>
            {trans(val || "SHELF")}
          </Tag>
        );
      },
    },
    { title: trans("Qty"), dataIndex: "quantity", width: 80, align: "center" },
    {
      title: trans("Expiry Date"),
      dataIndex: "expiryDate",
      width: 130,
      align: "center",
      render: (val) => (val ? dayjs(val).format("YYYY-MM-DD") : "—"),
    },
    {
      title: trans("Days"),
      dataIndex: "daysUntilExpiry",
      width: 100,
      align: "center",
      render: (val) => <DaysCell days={val} />,
    },
    {
      title: trans("Status"),
      dataIndex: "expiryStatus",
      width: 120,
      align: "center",
      render: (val) => <StatusTag status={val} />,
    },
  ];

  // ── Tab B: All Records ─────────────────────────────────────────────────────
  const itemColumns = [
    {
      title: trans("SKU Name"),
      key: "skuName",
      dataIndex: ["sku", "name"],
      width: 230,
      fixed: !isRTL ? "left" : undefined,
      sorter: true,
      filters: skuOptions,
      filterSearch: true,
      filterMultiple: true,
      filteredValue: tableSkuIds,
      onFilter: () => true,
      render: (val) => (
        <Tooltip title={val}>
          <Typography.Text strong ellipsis style={{ maxWidth: 210, display: "block" }}>{val}</Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: trans("Category"),
      key: "category",
      dataIndex: ["sku", "category"],
      width: 140,
      filters: categoryOptions,
      filterSearch: true,
      filterMultiple: true,
      filteredValue: tableCategories,
      onFilter: () => true,
      render: (cat) =>
        cat ? (
          <Tag color="purple" style={{ borderRadius: 10, fontWeight: 500 }}>
            {isRTL ? cat.name : (cat.nameEn || cat.name)}
          </Tag>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: trans("Location"),
      key: "location",
      dataIndex: ["poi", "name"],
      width: 180,
      filters: poiOptions,
      filterSearch: true,
      filterMultiple: true,
      filteredValue: tablePoiIds,
      onFilter: () => true,
      render: (val) => <span style={{ color: "#595959" }}>{val || "—"}</span>,
    },
    {
      title: trans("State"),
      key: "stateGeo",
      dataIndex: ["poi", "state"],
      width: 130,
      filters: stateOptions,
      filterSearch: true,
      filterMultiple: true,
      filteredValue: tableStates,
      onFilter: () => true,
      render: (val) =>
        val ? (
          <Tag color="blue" style={{ borderRadius: 10, fontWeight: 500 }}>{trans(val)}</Tag>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: trans("City"),
      key: "city",
      dataIndex: ["poi", "city"],
      width: 140,
      filters: cityOptions,
      filterSearch: true,
      filterMultiple: true,
      filteredValue: tableCities,
      onFilter: () => true,
      render: (val) =>
        val ? (
          <Tag color="cyan" style={{ borderRadius: 10, fontWeight: 500 }}>{trans(val)}</Tag>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: trans("Location Type"),
      key: "poiType",
      dataIndex: ["poi", "type"],
      width: 130,
      filters: poiTypeOptions,
      filterMultiple: true,
      filteredValue: tablePoiTypes,
      onFilter: () => true,
      render: (val) =>
        val ? (
          <Tag color="geekblue" style={{ borderRadius: 10, fontWeight: 500 }}>{trans(val)}</Tag>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: trans("Item Place"),
      key: "trackingContext",
      dataIndex: "trackingContext",
      width: 110,
      filters: itemPlaceOptions,
      filterMultiple: true,
      filteredValue: tableItemPlaces,
      onFilter: () => true,
      render: (val) => {
        const isShelf = (val || "").toUpperCase() === "SHELF";
        return (
          <Tag
            color={isShelf ? "cyan" : "volcano"}
            style={{ borderRadius: 10, fontWeight: 600 }}
          >
            {trans(val || "SHELF")}
          </Tag>
        );
      },
    },
    {
      title: trans("Qty"),
      key: "quantity",
      dataIndex: "quantity",
      width: 80,
      align: "center",
      sorter: true,
      render: (val) => <span style={{ fontWeight: 600 }}>{val}</span>,
    },
    {
      title: trans("Shelf Price"),
      key: "shelfPrice",
      dataIndex: "shelfPrice",
      width: 110,
      align: "center",
      render: (val) =>
        val != null ? (
          <span style={{ fontWeight: 600, color: "#389e0d" }}>{val}</span>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: trans("Case Pack"),
      key: "unitsPerContainer",
      dataIndex: "unitsPerContainer",
      width: 110,
      align: "center",
      render: (val, record) =>
        record.trackingContext === "SHELF"
          ? val != null ? <span style={{ fontWeight: 600 }}>{val}</span> : <span style={{ color: "#bbb" }}>—</span>
          : null,
    },
    {
      title: trans("Containers"),
      key: "shelfQtyContainers",
      dataIndex: "shelfQtyContainers",
      width: 110,
      align: "center",
      render: (val, record) =>
        record.trackingContext === "SHELF"
          ? val != null ? <span style={{ fontWeight: 600 }}>{val}</span> : <span style={{ color: "#bbb" }}>—</span>
          : null,
    },
    {
      title: trans("Production Date"),
      key: "productionDate",
      dataIndex: "productionDate",
      width: 140,
      align: "center",
      render: (val) => (val ? dayjs(val).format("YYYY-MM-DD") : <span style={{ color: "#bbb" }}>—</span>),
    },
    {
      title: trans("Expiry Date"),
      key: "expiryDate",
      dataIndex: "expiryDate",
      width: 130,
      align: "center",
      sorter: true,
      render: (val) => (val ? dayjs(val).format("YYYY-MM-DD") : <span style={{ color: "#bbb" }}>—</span>),
    },
    {
      title: trans("Days"),
      key: "daysUntilExpiry",
      dataIndex: "daysUntilExpiry",
      width: 90,
      align: "center",
      sorter: true,
      render: (val) => <DaysCell days={val} />,
    },
    {
      title: trans("Status"),
      key: "expiryStatus",
      dataIndex: "expiryStatus",
      width: 120,
      align: "center",
      filters: getStatusOptions(),
      filterMultiple: true,
      filteredValue: tableStatuses,
      onFilter: () => true,
      render: (val) => <StatusTag status={val} />,
    },
    {
      title: trans("Recorded By"),
      key: "recordedBy",
      dataIndex: ["createdBy", "name"],
      width: 150,
      filters: recordedByOptions,
      filterSearch: true,
      filterMultiple: true,
      filteredValue: tableRecordedByIds,
      onFilter: () => true,
      render: (val) => <span style={{ color: "#595959" }}>{val || "—"}</span>,
    },
    {
      title: trans("Recorded At"),
      key: "createdAt",
      dataIndex: "createdAt",
      width: 150,
      align: "center",
      render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "—"),
    },
  ];
  const visibleItemColumns = useReportTableColumns(itemColumns);

  // ── Table change handler (sort + column filters only) ──────────────────────
  // IMPORTANT: antd Table.onChange fires for pagination, filter AND sort changes.
  // Only call setFilters when a filter value actually changed — otherwise a
  // page-click event creates a new filters object → useEffect([filters]) resets
  // currentPage to 1, overriding the page the user just selected.
  const handleItemsTableChange = (pagination, tableFilters, sorter) => {
    if (sorter.order) {
      const sortKeyMap = { skuName: "skuName", quantity: "quantity", expiryDate: "expiryDate", daysUntilExpiry: "expiryDate" };
      const colKey = sorter.columnKey || (Array.isArray(sorter.field) ? sorter.field.join(",") : sorter.field);
      SetSortFilter({ by: sortKeyMap[colKey] || colKey, order: sorter.order === "ascend" ? "asc" : "desc" });
    } else if (sorter.field) {
      SetSortFilter({ by: "", order: "" });
    }

    const newSkuIds        = tableFilters.skuName          || null;
    const newPoiIds        = tableFilters.location          || null;
    const newStatuses      = tableFilters.expiryStatus      || null;
    const newRecordedByIds = tableFilters.recordedBy        || null;
    const newCategories    = tableFilters.category          || null;
    const newPoiTypes      = tableFilters.poiType           || null;
    const newItemPlaces    = tableFilters.trackingContext    || null;
    const newStates        = tableFilters.stateGeo          || null;
    let   newCities        = tableFilters.city              || null;

    // When the state selection changes, drop any city no longer under those states.
    const stateChanged = !arraysEqual(newStates, filters.tableStates);
    if (stateChanged && newStates && newStates.length && newCities) {
      const allowed = (data?.filterOptions?.cities || [])
        .filter(c => newStates.includes(c.state))
        .map(c => c.value);
      newCities = newCities.filter(c => allowed.includes(c));
      if (!newCities.length) newCities = null;
    }

    const filterChanged =
      !arraysEqual(newSkuIds,        filters.tableSkuIds) ||
      !arraysEqual(newPoiIds,        filters.tablePoiIds) ||
      !arraysEqual(newStatuses,      filters.tableStatuses) ||
      !arraysEqual(newRecordedByIds, filters.tableRecordedByIds) ||
      !arraysEqual(newCategories,    filters.tableCategories) ||
      !arraysEqual(newPoiTypes,      filters.tablePoiTypes) ||
      !arraysEqual(newItemPlaces,    filters.tableItemPlaces) ||
      !arraysEqual(newStates,        filters.tableStates) ||
      !arraysEqual(newCities,        filters.tableCities);

    if (filterChanged) {
      setFilters((prev) => ({
        ...prev,
        tableSkuIds:        newSkuIds,
        tablePoiIds:        newPoiIds,
        tableStatuses:      newStatuses,
        tableRecordedByIds: newRecordedByIds,
        tableCategories:    newCategories,
        tablePoiTypes:      newPoiTypes,
        tableItemPlaces:    newItemPlaces,
        tableStates:        newStates,
        tableCities:        newCities,
      }));
    }
  };

  // ── Tab definitions ────────────────────────────────────────────────────────
  const tabItems = [
    {
      key: "grouped",
      label: (
        <span style={{ padding: "0 8px", fontWeight: 500 }}>
          {trans("By Product")}
          {data?.grouped?.length ? (
            <Badge count={data.grouped.length} style={{ marginLeft: 8, backgroundColor: "#1890ff" }} />
          ) : null}
        </span>
      ),
      children: (
        <div style={{ padding: "0 0 8px" }}>
          <Table
            columns={groupedColumns}
            dataSource={data?.grouped || []}
            loading={isLoading}
            rowKey="skuId"
            size="middle"
            scroll={{ x: "max-content", y: 380 }}
            rowClassName={(record) => `expiry-row expiry-row--${record.worstStatus?.toLowerCase()}`}
            onRow={(record) => ({
              style: {
                borderLeft: `4px solid ${EXPIRY_COLORS[record.worstStatus]?.text || "#d9d9d9"}`,
              },
            })}
            expandable={{
              expandedRowRender: (record) => (
                <div style={{ padding: "12px 24px", background: "#fafafa", borderRadius: 6 }}>
                  <div style={{ marginBottom: 8, fontWeight: 600, color: "#595959", fontSize: 13 }}>
                    {trans("Locations")} ({record.locations?.length ?? 0})
                  </div>
                  <Table
                    columns={locationColumns}
                    dataSource={record.locations || []}
                    rowKey={(loc) => `${loc.poiId}|${loc.trackingContext}`}
                    pagination={false}
                    size="small"
                    onRow={(loc) => ({
                      style: {
                        background: EXPIRY_COLORS[loc.expiryStatus]?.bg || "#fff",
                      },
                    })}
                    style={{ borderRadius: 6, overflow: "hidden" }}
                  />
                </div>
              ),
              rowExpandable: (record) => (record.locations?.length ?? 0) > 0,
            }}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `${total} ${trans("items")}` }}
            style={{ borderRadius: 8, overflow: "hidden" }}
          />
        </div>
      ),
    },
    {
      key: "items",
      label: (
        <span style={{ padding: "0 8px", fontWeight: 500 }}>
          {trans("All Records")}
          {data?.pagination?.total ? (
            <Badge count={data.pagination.total} style={{ marginLeft: 8, backgroundColor: "#52c41a" }} overflowCount={9999} />
          ) : null}
        </span>
      ),
      children: (
        <div style={{ padding: "0 0 8px" }}>
          <AntdTable
            columns={visibleItemColumns}
            dataSource={data?.items || []}
            loading={isLoading}
            pageSize={limit}
            totalItems={data?.pagination?.total || 0}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
            onLimitChange={(newLimit) => { setLimit(newLimit); setCurrentPage(1); }}
            onTableChange={handleItemsTableChange}
            onRow={(record) => ({
              style: {
                borderLeft: `4px solid ${EXPIRY_COLORS[record.expiryStatus]?.text || "#d9d9d9"}`,
              },
            })}
            scrollY={380}
            scrollX
          />
        </div>
      ),
    },
  ];

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 10,
        boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
        overflow: "hidden",
        marginTop: 12,
      }}
    >
      <Tabs
        defaultActiveKey="grouped"
        items={tabItems}
        tabBarStyle={{
          margin: 0,
          padding: "0 16px",
          background: "#fafafa",
          borderBottom: "1px solid #f0f0f0",
        }}
        style={{ minHeight: 480 }}
      />
    </div>
  );
};

export default ProductExpirationReportTable;
